import { useState, useMemo, useEffect } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { Layout } from '@/client/layouts/layout';
import { API_BASE_URL } from '@/client/config';
import { getMonthName, formatDuration } from '@/client/lib/date';
import { parseString, parseBoolean, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { formatStreamField } from '@/client/lib/format';
import { downloadCSV } from '@/client/lib/csv';
import { Download, FileSpreadsheet } from 'lucide-react';
import type { ReportsResponse, ByEncoderReportsResponse, EncoderLiveStreamData, EncoderVODData, AllClientsReportsResponse } from '@shared/types';
import type { ClientResponse, ClientCategoryResponse, GetClientCategoriesResponse } from '@shared/types';

import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Label } from '@/client/components/ui/label';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type SortDirection,
} from '@/client/components/ui/table';
import { ClientSelector } from '@/client/components/client-selector';
import { CategorySelector } from '@/client/components/category-selector';
import { DateRangePicker } from '@/client/components/date-range-picker';

import { EncoderLiveStreamsTable, type LiveStreamSortField } from './components/EncoderLiveStreamsTable';
import { EncoderVODTable, type VODSortField } from './components/EncoderVODTable';
import { FilterSummary } from './components/FilterSummary';

type ReportsSearch = {
  clientID?: string;
  categoryID?: string;
  startDate?: string;
  endDate?: string;
  quickRange?: string;
  groupByEncoder?: boolean;
  allClients?: boolean;
};

// Stable empty reference so derived `categories` keeps identity across renders
const EMPTY_CATEGORIES: ClientCategoryResponse[] = [];

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/reports',
  validateSearch: (search: Record<string, unknown>): ReportsSearch => ({
    clientID: parseString(search.clientID),
    categoryID: parseString(search.categoryID),
    startDate: parseString(search.startDate),
    endDate: parseString(search.endDate),
    quickRange: parseString(search.quickRange),
    groupByEncoder: parseBoolean(search.groupByEncoder, false),
    allClients: parseBoolean(search.allClients, false),
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: ReportsComponent,
});

/** Sort encoder data by field */
function sortEncoderData<T extends EncoderLiveStreamData | EncoderVODData>(
  data: T[],
  sortField: string | null,
  sortDirection: SortDirection
): T[] {
  if (!sortField) return data;

  return [...data].sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (sortField) {
      case 'simpleName':
        aVal = a.simpleName.toLowerCase();
        bVal = b.simpleName.toLowerCase();
        break;
      case 'distinctEventCount':
        aVal = (a as EncoderLiveStreamData).distinctEventCount;
        bVal = (b as EncoderLiveStreamData).distinctEventCount;
        break;
      case 'publishedEventCount':
        aVal = (a as EncoderVODData).publishedEventCount;
        bVal = (b as EncoderVODData).publishedEventCount;
        break;
      case 'totalDurationMinutes':
        aVal = a.totalDurationMinutes;
        bVal = b.totalDurationMinutes;
        break;
      case 'bitrate':
        aVal = a.streamInfo?.bitrate ?? 0;
        bVal = b.streamInfo?.bitrate ?? 0;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
}

function ReportsComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const {
    clientID: selectedClientId = '',
    categoryID: selectedCategoryId,
    startDate = '',
    endDate = '',
    quickRange = '',
    groupByEncoder = false,
    allClients = false,
  } = useSearch({ from: '/events/reports' });

  // Persist search params across navigation
  usePersistedSearch('/events/reports', {
    clientID: undefined, categoryID: undefined, startDate: undefined, endDate: undefined, quickRange: undefined, groupByEncoder: false, allClients: false,
  });

  // Client object state (not in URL, derived from clientID)
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);

  // Per-client category cache (not in URL); avoids refetching a client already loaded
  const [categoriesByClient, setCategoriesByClient] = useState<Record<string, ClientCategoryResponse[]>>({});
  const categories = selectedClientId ? (categoriesByClient[selectedClientId] ?? EMPTY_CATEGORIES) : EMPTY_CATEGORIES;

  // Helper to update URL with new search params
  // Uses functional update to get latest search state and avoid stale closure issues
  const updateSearchParams = (updates: Partial<ReportsSearch>) => {
    navigate({
      to: '/events/reports',
      search: (prev) => buildSearchParams({
        clientID: prev.clientID || undefined,
        categoryID: prev.categoryID || undefined,
        startDate: prev.startDate || undefined,
        endDate: prev.endDate || undefined,
        quickRange: prev.quickRange || undefined,
        groupByEncoder: prev.groupByEncoder || undefined,
        allClients: prev.allClients || undefined,
        ...updates,
      }),
    });
  };

  // Setters that update URL
  const setStartDate = (value: string) => updateSearchParams({ startDate: value || undefined });
  const setEndDate = (value: string) => updateSearchParams({ endDate: value || undefined });
  const setQuickRange = (value: string) => updateSearchParams({ quickRange: value || undefined });
  const setGroupByEncoder = (value: boolean) => updateSearchParams({ groupByEncoder: value || undefined });
  const setCategoryID = (value: string | undefined) => updateSearchParams({ categoryID: value || undefined });

  // Report state
  const [reportsData, setReportsData] = useState<ReportsResponse | null>(null);
  const [encoderReportsData, setEncoderReportsData] = useState<ByEncoderReportsResponse | null>(null);
  const [allClientsData, setAllClientsData] = useState<AllClientsReportsResponse | null>(null);

  // Sort state for encoder tables - default to Encoder A-Z
  const [liveStreamSort, setLiveStreamSort] = useState<LiveStreamSortField>('simpleName');
  const [liveStreamSortDir, setLiveStreamSortDir] = useState<SortDirection>('asc');
  const [vodSort, setVodSort] = useState<VODSortField>('simpleName');
  const [vodSortDir, setVodSortDir] = useState<SortDirection>('asc');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  // Fetch a client's categories once and cache them; skip if already loaded or in all-clients mode
  useEffect(() => {
    if (!selectedClientId || allClients) return;
    if (categoriesByClient[selectedClientId]) return;
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${API_BASE_URL}/api/clients/${selectedClientId}/categories`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (!response.ok) throw new Error(`Failed to fetch categories: ${response.statusText}`);
        const data: GetClientCategoriesResponse = await response.json();
        if (!cancelled) {
          setCategoriesByClient((prev) => ({ ...prev, [selectedClientId]: data.categories }));
        }
      } catch (err) {
        console.error('Error fetching client categories:', err);
      }
    };
    loadCategories();
    return () => { cancelled = true; };
  }, [selectedClientId, allClients, categoriesByClient, getAccessTokenSilently]);

  // Selected category object (for header display), derived from URL + fetched list
  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.categoryID) === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  // Can generate report if dates are set AND (allClients OR specific client selected)
  const canGenerateReport = startDate && endDate && (allClients || selectedClientId);

  // Sorted encoder data
  const sortedLiveStreams = useMemo(() => {
    if (!encoderReportsData) return [];
    return sortEncoderData(encoderReportsData.liveStreams.byEncoder, liveStreamSort, liveStreamSortDir);
  }, [encoderReportsData, liveStreamSort, liveStreamSortDir]);

  const sortedVOD = useMemo(() => {
    if (!encoderReportsData) return [];
    return sortEncoderData(encoderReportsData.vod.byEncoder, vodSort, vodSortDir);
  }, [encoderReportsData, vodSort, vodSortDir]);

  // Event handlers
  const handleClientChange = (clientId: string, client: ClientResponse | null) => {
    // Clear category: a category from the previous client does not apply to the new one
    updateSearchParams({ clientID: clientId || undefined, categoryID: undefined });
    setSelectedClient(client);
    setReportsData(null);
    setEncoderReportsData(null);
    setAllClientsData(null);
    setConfirmPending(false);
  };

  const handleAllClientsChange = (checked: boolean) => {
    // All-clients mode has no per-client category, so clear any selection
    updateSearchParams({ allClients: checked || undefined, categoryID: undefined });
    setReportsData(null);
    setEncoderReportsData(null);
    setAllClientsData(null);
    setConfirmPending(false);
  };

  const handleCategoryChange = (value: string | undefined) => {
    setCategoryID(value);
    setReportsData(null);
    setEncoderReportsData(null);
    setAllClientsData(null);
    setConfirmPending(false);
  };

  const handleGroupByEncoderChange = (checked: boolean) => {
    setGroupByEncoder(checked);
    setReportsData(null);
    setEncoderReportsData(null);
    setAllClientsData(null);
    setConfirmPending(false);
    // Reset sort state to default (Encoder A-Z)
    setLiveStreamSort('simpleName');
    setLiveStreamSortDir('asc');
    setVodSort('simpleName');
    setVodSortDir('asc');
  };

  const handleLiveStreamSort = (field: LiveStreamSortField) => {
    if (liveStreamSort === field) {
      setLiveStreamSortDir(liveStreamSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setLiveStreamSort(field);
      setLiveStreamSortDir('desc');
    }
  };

  const handleVodSort = (field: VODSortField) => {
    if (vodSort === field) {
      setVodSortDir(vodSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setVodSort(field);
      setVodSortDir('desc');
    }
  };

  const handleGenerateReport = async () => {
    if (!canGenerateReport) return;

    if (!confirmPending) {
      setConfirmPending(true);
      return;
    }

    setConfirmPending(false);
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      if (allClients) {
        // All clients mode
        const response = await fetch(
          `${API_BASE_URL}/api/events/reports/all?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch reports: ${response.statusText}`);
        }

        const data: AllClientsReportsResponse = await response.json();
        setAllClientsData(data);
        setReportsData(null);
        setEncoderReportsData(null);
      } else {
        // Single client mode
        const endpoint = groupByEncoder
          ? `${API_BASE_URL}/api/events/reports/by-encoder`
          : `${API_BASE_URL}/api/events/reports`;

        const categoryParam = selectedCategoryId ? `&categoryID=${selectedCategoryId}` : '';
        const response = await fetch(
          `${endpoint}?clientID=${selectedClientId}&startDate=${startDate}&endDate=${endDate}${categoryParam}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch reports: ${response.statusText}`);
        }

        if (groupByEncoder) {
          const data: ByEncoderReportsResponse = await response.json();
          setEncoderReportsData(data);
          setReportsData(null);
        } else {
          const data: ReportsResponse = await response.json();
          setReportsData(data);
          setEncoderReportsData(null);
        }
        setAllClientsData(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // CSV Export handlers
  const getClientFileName = () => selectedClient?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'client';

  // Category label (display name preferred), or null when no category filter is applied
  const categoryLabel = selectedCategory
    ? (selectedCategory.displayName?.trim() || selectedCategory.categoryName)
    : null;

  // Filename suffix for the applied category, e.g. "_City_Council" (empty when none)
  const getCategorySuffix = () => (categoryLabel ? `_${categoryLabel.replace(/[^a-zA-Z0-9]/g, '_')}` : '');

  // Preamble rows describing the applied filters, written above the CSV data table
  const buildReportMeta = (start?: string, end?: string): string[][] => {
    const meta: string[][] = [];
    meta.push(['Client', selectedClient ? `${selectedClient.name} (${selectedClientId})` : selectedClientId]);
    if (start && end) meta.push(['Date Range', `${start} to ${end}`]);
    if (categoryLabel) meta.push(['Category', categoryLabel]);
    return meta;
  };

  const handleExportLiveStreams = () => {
    if (!reportsData) return;
    const headers = ['Year', 'Month', 'Distinct Events', 'Total Duration (minutes)', 'Avg Duration (minutes)'];
    const rows: string[][] = reportsData.liveStreams.monthly.map(row => [
      row.year.toString(),
      getMonthName(row.month),
      row.distinctEventCount.toString(),
      row.totalDurationMinutes.toString(),
      row.avgDurationMinutes.toString(),
    ]);
    rows.push(['TOTAL', '', reportsData.liveStreams.totals.distinctEventCount.toString(), reportsData.liveStreams.totals.totalDurationMinutes.toString(), reportsData.liveStreams.totals.avgDurationMinutes.toString()]);
    downloadCSV(`live_streams_${getClientFileName()}${getCategorySuffix()}_${reportsData.dateRange.start}_to_${reportsData.dateRange.end}.csv`, headers, rows, buildReportMeta(reportsData.dateRange.start, reportsData.dateRange.end));
  };

  const handleExportVOD = () => {
    if (!reportsData) return;
    const headers = ['Year', 'Month', 'Published Events', 'Total Duration (minutes)', 'Avg Duration (minutes)'];
    const rows: string[][] = reportsData.vod.monthly.map(row => [
      row.year.toString(),
      getMonthName(row.month),
      row.publishedEventCount.toString(),
      row.totalDurationMinutes.toString(),
      row.avgDurationMinutes.toString(),
    ]);
    rows.push(['TOTAL', '', reportsData.vod.totals.publishedEventCount.toString(), reportsData.vod.totals.totalDurationMinutes.toString(), reportsData.vod.totals.avgDurationMinutes.toString()]);
    downloadCSV(`vod_${getClientFileName()}${getCategorySuffix()}_${reportsData.dateRange.start}_to_${reportsData.dateRange.end}.csv`, headers, rows, buildReportMeta(reportsData.dateRange.start, reportsData.dateRange.end));
  };

  const handleExportLiveStreamsByEncoder = () => {
    if (!encoderReportsData) return;
    const headers = ['Encoder', 'Resolution', 'Bitrate', 'Frame Rate', 'Aspect Ratio', 'Captions', 'Distinct Events', 'Total Duration (minutes)', 'Avg Duration (minutes)'];
    const rows: string[][] = encoderReportsData.liveStreams.byEncoder.map(row => [
      row.simpleName,
      formatStreamField(row.streamInfo, 'resolution'),
      formatStreamField(row.streamInfo, 'bitrate'),
      formatStreamField(row.streamInfo, 'frameRate'),
      formatStreamField(row.streamInfo, 'aspectRatio'),
      formatStreamField(row.streamInfo, 'hasCaptions'),
      row.distinctEventCount.toString(),
      row.totalDurationMinutes.toString(),
      row.avgDurationMinutes.toString(),
    ]);
    rows.push(['TOTAL', '', '', '', '', '', encoderReportsData.liveStreams.totals.distinctEventCount.toString(), encoderReportsData.liveStreams.totals.totalDurationMinutes.toString(), encoderReportsData.liveStreams.totals.avgDurationMinutes.toString()]);
    downloadCSV(`live_streams_by_encoder_${getClientFileName()}${getCategorySuffix()}_${encoderReportsData.dateRange.start}_to_${encoderReportsData.dateRange.end}.csv`, headers, rows, buildReportMeta(encoderReportsData.dateRange.start, encoderReportsData.dateRange.end));
  };

  const handleExportVODByEncoder = () => {
    if (!encoderReportsData) return;
    const headers = ['Encoder', 'Resolution', 'Bitrate', 'Frame Rate', 'Aspect Ratio', 'Captions', 'Published Events', 'Total Duration (minutes)', 'Avg Duration (minutes)'];
    const rows: string[][] = encoderReportsData.vod.byEncoder.map(row => [
      row.simpleName,
      formatStreamField(row.streamInfo, 'resolution'),
      formatStreamField(row.streamInfo, 'bitrate'),
      formatStreamField(row.streamInfo, 'frameRate'),
      formatStreamField(row.streamInfo, 'aspectRatio'),
      formatStreamField(row.streamInfo, 'hasCaptions'),
      row.publishedEventCount.toString(),
      row.totalDurationMinutes.toString(),
      row.avgDurationMinutes.toString(),
    ]);
    rows.push(['TOTAL', '', '', '', '', '', encoderReportsData.vod.totals.publishedEventCount.toString(), encoderReportsData.vod.totals.totalDurationMinutes.toString(), encoderReportsData.vod.totals.avgDurationMinutes.toString()]);
    downloadCSV(`vod_by_encoder_${getClientFileName()}${getCategorySuffix()}_${encoderReportsData.dateRange.start}_to_${encoderReportsData.dateRange.end}.csv`, headers, rows, buildReportMeta(encoderReportsData.dateRange.start, encoderReportsData.dateRange.end));
  };

  // CSV Export - All Clients
  const handleExportAllClients = () => {
    if (!allClientsData) return;
    const headers = ['Client ID', 'Client Name', 'Live Stream Events', 'Live Stream Hours', 'VOD Events', 'VOD Hours'];
    const rows: string[][] = allClientsData.clients.map(client => [
      client.clientID,
      client.clientName,
      client.liveStreamEventCount.toString(),
      client.liveStreamDurationHours.toFixed(2),
      client.vodEventCount.toString(),
      client.vodDurationHours.toFixed(2),
    ]);
    rows.push([
      'GRAND TOTAL',
      `(${allClientsData.grandTotals.clientCount} clients)`,
      allClientsData.grandTotals.liveStreamEventCount.toString(),
      allClientsData.grandTotals.liveStreamDurationHours.toFixed(2),
      allClientsData.grandTotals.vodEventCount.toString(),
      allClientsData.grandTotals.vodDurationHours.toFixed(2),
    ]);
    downloadCSV(`event_reports_all_clients_${allClientsData.dateRange.start}_to_${allClientsData.dateRange.end}.csv`, headers, rows);
  };

  // Computed values
  const cacheTimestamp = allClients
    ? allClientsData?.cacheInfo?.cachedAt
    : groupByEncoder
      ? encoderReportsData?.cacheInfo?.cachedAt
      : reportsData?.cacheInfo?.cachedAt;
  const dateRange = allClients
    ? allClientsData?.dateRange
    : groupByEncoder
      ? encoderReportsData?.dateRange
      : reportsData?.dateRange;
  const clientID = groupByEncoder ? encoderReportsData?.clientID : reportsData?.clientID;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-semibold">Event Reports</h3>
          <CacheTimestamp cachedAt={cacheTimestamp ?? null} />
        </div>

        {/* Controls Section - Option B+D: Labeled Section Groups in Card */}
        <div className="border rounded-lg p-4 space-y-4 bg-card">
          {/* Date Range Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Date Range</span>
            <div className="flex flex-wrap gap-4 items-end">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                quickRange={quickRange}
                onQuickRangeChange={setQuickRange}
                onDateChange={() => setConfirmPending(false)}
              />
            </div>
          </div>

          {/* Client & Options Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Client & Options</span>
            <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="all-clients"
                  checked={allClients}
                  onCheckedChange={(checked) => handleAllClientsChange(checked === true)}
                />
                <Label htmlFor="all-clients" className="cursor-pointer">
                  All Clients
                </Label>
              </div>

              <span className="text-muted-foreground">or</span>

              <ClientSelector
                value={selectedClientId}
                onChange={handleClientChange}
                className="min-w-[250px]"
                label=""
                disabled={allClients}
              />

              <CategorySelector
                categories={categories}
                value={selectedCategoryId}
                onChange={handleCategoryChange}
                disabled={allClients || !selectedClientId}
                className="min-w-[220px]"
              />

              <div className="flex items-center gap-2">
                <Checkbox
                  id="groupByEncoder"
                  checked={groupByEncoder}
                  onCheckedChange={handleGroupByEncoderChange}
                  disabled={allClients}
                />
                <Label htmlFor="groupByEncoder" className={`cursor-pointer ${allClients ? 'text-muted-foreground' : ''}`}>
                  Group by Encoder
                </Label>
              </div>

              <Button
                onClick={handleGenerateReport}
                disabled={!canGenerateReport || loading}
                variant={confirmPending ? 'destructive' : 'default'}
                className="min-w-[175px]"
              >
                {loading ? 'Loading...' : confirmPending ? 'Click to Confirm' : (
                  <>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>

              <ResetFiltersButton
                routePath="/events/reports"
                defaultValues={{ clientID: undefined, categoryID: undefined, startDate: undefined, endDate: undefined, quickRange: undefined, groupByEncoder: false, allClients: false }}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded">
            <p className="m-0 text-red-800 font-medium">Error: {error}</p>
          </div>
        )}

        {/* Monthly Results Section */}
        {reportsData && !groupByEncoder && !allClients && (
          <div className="space-y-8">
            <FilterSummary
              clientID={clientID}
              start={dateRange?.start}
              end={dateRange?.end}
              categoryName={selectedCategory ? (selectedCategory.displayName?.trim() || selectedCategory.categoryName) : null}
            />

            {/* Live Streams Table */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Live Streams</h4>
                <Button variant="outline" size="sm" onClick={handleExportLiveStreams}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Distinct Events</TableHead>
                      <TableHead className="text-right">Total Duration</TableHead>
                      <TableHead className="text-right">Avg Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsData.liveStreams.monthly.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No live stream data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {reportsData.liveStreams.monthly.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.year}</TableCell>
                            <TableCell>{getMonthName(row.month)}</TableCell>
                            <TableCell className="text-right">{row.distinctEventCount}</TableCell>
                            <TableCell className="text-right">{formatDuration(row.totalDurationMinutes)}</TableCell>
                            <TableCell className="text-right">{formatDuration(row.avgDurationMinutes)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell colSpan={2}>Total</TableCell>
                          <TableCell className="text-right">{reportsData.liveStreams.totals.distinctEventCount}</TableCell>
                          <TableCell className="text-right">{formatDuration(reportsData.liveStreams.totals.totalDurationMinutes)}</TableCell>
                          <TableCell className="text-right">{formatDuration(reportsData.liveStreams.totals.avgDurationMinutes)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* VOD Table */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">VOD (Published Events)</h4>
                <Button variant="outline" size="sm" onClick={handleExportVOD}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Published Events</TableHead>
                      <TableHead className="text-right">Total Duration</TableHead>
                      <TableHead className="text-right">Avg Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsData.vod.monthly.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No VOD data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {reportsData.vod.monthly.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.year}</TableCell>
                            <TableCell>{getMonthName(row.month)}</TableCell>
                            <TableCell className="text-right">{row.publishedEventCount}</TableCell>
                            <TableCell className="text-right">{formatDuration(row.totalDurationMinutes)}</TableCell>
                            <TableCell className="text-right">{formatDuration(row.avgDurationMinutes)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell colSpan={2}>Total</TableCell>
                          <TableCell className="text-right">{reportsData.vod.totals.publishedEventCount}</TableCell>
                          <TableCell className="text-right">{formatDuration(reportsData.vod.totals.totalDurationMinutes)}</TableCell>
                          <TableCell className="text-right">{formatDuration(reportsData.vod.totals.avgDurationMinutes)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* By-Encoder Results Section */}
        {encoderReportsData && groupByEncoder && !allClients && (
          <div className="space-y-8">
            <FilterSummary
              clientID={clientID}
              start={dateRange?.start}
              end={dateRange?.end}
              categoryName={selectedCategory ? (selectedCategory.displayName?.trim() || selectedCategory.categoryName) : null}
            />

            <EncoderLiveStreamsTable
              data={sortedLiveStreams}
              totals={encoderReportsData.liveStreams.totals}
              sortField={liveStreamSort}
              sortDirection={liveStreamSortDir}
              onSort={handleLiveStreamSort}
              onExport={handleExportLiveStreamsByEncoder}
            />

            <EncoderVODTable
              data={sortedVOD}
              totals={encoderReportsData.vod.totals}
              multiEncoderEventCount={encoderReportsData.vod.multiEncoderEventCount}
              sortField={vodSort}
              sortDirection={vodSortDir}
              onSort={handleVodSort}
              onExport={handleExportVODByEncoder}
            />
          </div>
        )}

        {/* All Clients Results Section */}
        {allClientsData && allClients && (
          <div className="space-y-8">
            <div className="text-sm text-muted-foreground">
              All Clients Report from <Badge variant="outline" className="text-xs font-mono">{allClientsData.dateRange.start} to {allClientsData.dateRange.end}</Badge>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Event Reports - All Clients</h4>
                <Button variant="outline" size="sm" onClick={handleExportAllClients}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Client Name</TableHead>
                      <TableHead className="text-right">Live Stream Events</TableHead>
                      <TableHead className="text-right">Live Stream Hours</TableHead>
                      <TableHead className="text-right">VOD Events</TableHead>
                      <TableHead className="text-right">VOD Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allClientsData.clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No event data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {allClientsData.clients.map((client) => (
                          <TableRow key={client.clientID}>
                            <TableCell>{client.clientID}</TableCell>
                            <TableCell>{client.clientName}</TableCell>
                            <TableCell className="text-right">{client.liveStreamEventCount.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{client.liveStreamDurationHours.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{client.vodEventCount.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{client.vodDurationHours.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell>Grand Total</TableCell>
                          <TableCell className="text-muted-foreground">
                            ({allClientsData.grandTotals.clientCount} clients)
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.liveStreamEventCount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.liveStreamDurationHours.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.vodEventCount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.vodDurationHours.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
